import { forwardRef } from "react";
import { WidgetView } from "./WidgetView";
import type { ReportConfig, WidgetConfig, WidgetSize } from "./types";
import { useTranslation } from "../../i18n";
import type { LangCode } from "../../i18n";

const SIZE_SPAN: Record<WidgetSize, string> = {
  small: "md:col-span-2",
  medium: "md:col-span-3",
  large: "md:col-span-6",
};

// Widget types that render their own headings / shouldn't get a card title bar.
const HEADERLESS = new Set(["text"]);

function dateShort(s: string, lang: LangCode): string {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", year: "numeric" }).format(d);
}

function rangeText(
  r: ReportConfig["dateRange"],
  lang: LangCode,
  t: (key: string, fallback?: string) => string
): string {
  if (r.start && r.end) return `${dateShort(r.start, lang)} – ${dateShort(r.end, lang)}`;
  if (r.start) return t("reportBuilder.dateFrom", "From {date}").replace("{date}", dateShort(r.start, lang));
  if (r.end) return t("reportBuilder.dateThrough", "Through {date}").replace("{date}", dateShort(r.end, lang));
  return t("reportBuilder.allTime", "All time");
}

function PreviewCard({ w }: { w: WidgetConfig }) {
  const headerless = HEADERLESS.has(w.type) || w.type === "metric";
  return (
    <div className={`${SIZE_SPAN[w.size]} break-inside-avoid rounded-xl border border-gray-200 bg-white p-4 shadow-sm`}>
      {!headerless && (
        <h3 className="mb-3 border-b border-gray-100 pb-2 text-sm font-bold text-gray-900">{w.title}</h3>
      )}
      <WidgetView config={w} />
    </div>
  );
}

export const ReportPreview = forwardRef<HTMLDivElement, { report: ReportConfig }>(function ReportPreview(
  { report },
  ref
) {
  const { t, lang } = useTranslation();
  return (
    <div ref={ref} className="report-preview mx-auto max-w-5xl bg-white">
      {/* Branded header */}
      <div className="relative overflow-hidden rounded-t-xl bg-gradient-to-r from-blue-900 to-blue-700 px-8 py-7 text-white">
        <div className="absolute inset-x-0 bottom-0 h-1 bg-cyan-400" />
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold leading-tight">{report.title || t("reportBuilder.untitledReport", "Untitled Report")}</h1>
            {report.subtitle && <p className="mt-1 text-sm text-blue-100">{report.subtitle}</p>}
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-blue-200">
              <span>{t("reportBuilder.preparedBy", "Prepared by {name}").replace("{name}", report.author || "—")}</span>
              <span>{t("reportBuilder.period", "Period: {range}").replace("{range}", rangeText(report.dateRange, lang, t))}</span>
            </div>
          </div>
          {report.includeLogo && (
            <div className="flex shrink-0 items-center gap-2">
              <img src="/g-data-logo-no-bg.png" alt="G-Data Labs" crossOrigin="anonymous" className="h-12 w-12 object-contain" />
              <div className="leading-tight">
                <div className="text-sm font-bold text-white">GuardAI</div>
                <div className="text-[0.65rem] text-cyan-200">by G-Data Labs</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="rounded-b-xl border border-t-0 border-gray-200 bg-gray-50/50 px-6 py-6">
        {report.widgets.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t("reportBuilder.noWidgetsYet", "This report has no widgets yet.")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
            {report.widgets.map((w) => (
              <PreviewCard key={w.id} w={w} />
            ))}
          </div>
        )}
        <div className="mt-8 border-t border-gray-200 pt-4 text-center text-[0.65rem] text-gray-400">
          {t("reportBuilder.generatedBy", "Generated by GuardAI · © {year} G-Data Labs. All rights reserved.").replace("{year}", String(new Date().getFullYear()))}
        </div>
      </div>
    </div>
  );
});
